import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

const STORAGE_KEY = "virsa_cookie_consent";

// Shown once per browser until accepted — required disclosure before running
// any ad network (Google AdSense reviews the live site for this before
// approving an account), and honest even today since local storage already
// remembers theme/recent-searches.
export default function CookieConsent() {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  function accept() {
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
  }

  // Portaled — nothing currently sits between App.js's root and this
  // component that would hijack position:fixed (verified live), but that's
  // an assumption about the rest of the app that could silently break with
  // some future change elsewhere. Every other fixed overlay in the app
  // portals to document.body for exactly this reason (see Toast.jsx) — no
  // reason for this one to be the exception that depends on staying lucky.
  return createPortal(
    <div style={banner}>
      <p style={text}>
        We use cookies and local storage to run GatherYou and, once ads are
        enabled, Google and its partners may use cookies to show relevant
        ads. See our{" "}
        <Link to="/privacy" style={link}>
          Privacy Policy
        </Link>{" "}
        for details.
      </p>
      <button style={acceptBtn} onClick={accept}>
        Accept
      </button>
    </div>,
    document.body
  );
}

const banner = {
  position: "fixed",
  left: 0,
  right: 0,
  // The pill nav already needs clearing (see 78px offset), so the banner no
  // longer sits flush against the screen edge — an extra safe-area padding
  // on top of that here would just double up and pad the banner out
  // needlessly on devices with a tall gesture-bar inset.
  bottom: "calc(78px + env(safe-area-inset-bottom))",
  // Below Sheet (1100) — a real sheet/modal (e.g. Profile's "Edit profile")
  // needs to cover this, or a first-time visitor who hasn't dismissed the
  // banner yet can find it sitting on top of the sheet's own footer
  // buttons. 1150 was a mistake (it's numerically ABOVE Sheet's 1100, the
  // opposite of what this comment always said) — 1050 actually is below it,
  // while still clear of pillNav's 1000.
  zIndex: 1050,
  display: "flex",
  alignItems: "center",
  gap: 14,
  flexWrap: "wrap",
  padding: "12px 16px",
  background: "var(--bg-surface)",
  borderTop: "1px solid var(--border-soft)",
  boxShadow: "0 -4px 16px rgba(0,0,0,0.12)",
};

const text = {
  flex: "1 1 260px",
  margin: 0,
  fontSize: 12.5,
  color: "var(--text-secondary)",
  lineHeight: 1.5,
};

const link = { color: "var(--accent-link)", fontWeight: 600 };

const acceptBtn = {
  padding: "8px 18px",
  borderRadius: 10,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer",
  flexShrink: 0,
};
